import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  DOCUMENT,
} from '@angular/core';
import { SidebarModule } from 'primeng/sidebar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserProfileComponent } from '../user-profile/user-profile.component';
import { Location } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  UsersManagementListFilters,
  UsersManagementListSortBy,
} from '../../../services/users-management/users-management.store.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { UsersManagementUserAvatarComponent } from './users-management-user-avatar.component';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TooltipModule } from 'primeng/tooltip';
import {
  Activity,
  Group,
  UserDetail,
  UsersManagementDetailComponent,
} from '../../components/users-management-detail/users-management-detail.component';
import { UsersManagementStoreService } from '../../../services/users-management/users-management.store.service';
import { DeleteUserConfirmDialogComponent } from '../../components/delete-user-confirm-dialog/delete-user-confirm-dialog.component';
import { DeleteUserBlockedDialogComponent } from '../../components/delete-user-blocked-dialog/delete-user-blocked-dialog.component';
import { AccountSuspensionReason } from '../../utils/an-account-suspension';
import {
  buildDeleteUserBlockedDialogContent,
  DeleteUserBlockedDialogContent,
  extractDeleteUserErrorBody,
  getDeleteUserErrorMessage,
  isDeleteUserBlocked,
} from '../../utils/delete-user-error.util';
import { ActivityDrawerHostComponent } from '../../components/activity-drawer-host/activity-drawer-host.component';
import { GroupsManagementComponent } from '../groups-management/groups-management.component';
import { ActivitiesManagementApiService } from '../../../services/activities-management/activities-management-api.service';
import { DEFAULT_GROUP_COVER_URL } from '../../constants/management-ui.constants';
import type { SportOption, SportTypeConfig } from '../../models/management.model';
import { toTwoColumnOptions } from '../../utils/management-options-layout.util';
import { getUserTypeIconPath, USER_TYPE_FILTER_OPTIONS } from './users-management-user-type';
import { MANAGEMENT_USER_QUERY_KEY } from '../../utils/management-user-return.util';
import {
  CreditOnDeletion,
  readCreditOnDeletion,
} from '../../../services/users-management/what-the-deletion-does-with-the-credit';

@Component({
  selector: 'bo-users-management',
  templateUrl: './users-management.component.html',
  styleUrl: './users-management.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    SidebarModule,
    UserProfileComponent,
    CommonModule,
    FormsModule,
    TableModule,
    UsersManagementUserAvatarComponent,
    DropdownModule,
    CalendarModule,
    TooltipModule,
    UsersManagementDetailComponent,
    DeleteUserConfirmDialogComponent,
    DeleteUserBlockedDialogComponent,
    ActivityDrawerHostComponent,
    GroupsManagementComponent,
  ],
})
export class UsersManagementComponent implements OnInit, OnDestroy {
  #store = inject(UsersManagementStoreService);
  #translate = inject(TranslateService);
  #activitiesApi = inject(ActivitiesManagementApiService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #location = inject(Location);

  /** Debounce before calling filter API (ms). */
  private readonly FILTER_DEBOUNCE_MS = 300;
  private readonly destroy$ = new Subject<void>();
  private readonly filterChanges$ = new Subject<void>();

  showFilters = true;
  searchQuery = '';
  /** Exact city name. The API matches it case-insensitively. */
  cityFilter = '';
  /**
   * Profile completion bounds, in percent. Left empty they are not sent at all,
   * so an untouched filter never narrows the list.
   */
  completionMin: number | null = null;
  completionMax: number | null = null;
  startDate: Date | null = null;
  endDate: Date | null = null;
  startLoginDate: Date | null = null;
  endLoginDate: Date | null = null;
  selectedUserType = '';
  selectedStatus = '';
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'info' | 'error' = 'info';
  private _toastTimer: ReturnType<typeof setTimeout> | null = null;
  drawerVisible = false;
  selectedUser: UserDetail | null = null;
  showDeleteDialog = false;
  showDeleteBlockedDialog = false;
  deleteBlockedContent: DeleteUserBlockedDialogContent | null = null;
  suspendingUser = false;
  deletingUser = false;
  drawerInitialMode: 'view' | 'edit' | 'delete' = 'view';
  deleteConfirmed = false;
  /** Read from the deletion check, so the admin sees the amounts before confirming. */
  creditOnDeletion: CreditOnDeletion | null = null;
  toastUserName = '';
  loading = this.#store.loading;
  page = 1;
  pageSize = 10;
  first = 0;
  sortField = 'createdAt';
  sortOrder: 1 | -1 = -1;

  @ViewChild('userActivityDrawer') userActivityDrawer?: ActivityDrawerHostComponent;
  @ViewChild('userGroupDrawers') userGroupDrawers?: GroupsManagementComponent;

  sportTypeConfigs: SportTypeConfig[] = [];
  sportOptions: SportOption[] = [];
  defaultCoverPhotoUrl = DEFAULT_GROUP_COVER_URL;
  private readonly drawerSubscriptions = new Subscription();

  constructor(private cdr: ChangeDetectorRef, @Inject(DOCUMENT) private document: Document) {}

  userTypeOptions = USER_TYPE_FILTER_OPTIONS;
  getUserTypeIconPath = getUserTypeIconPath;

  statusOptions: { label: string; value: string }[] = [];
  notificationOptions: { label: string; value: '' | 'true' | 'false' }[] = [];

  /** Dropdown labels live in the dictionaries and rebake on language switch. */
  private buildFilterOptions(): void {
    const t = (key: string) => this.#translate.instant(key);
    this.statusOptions = [
      { label: t('usersManagement.allStatuses'), value: '' },
      { label: t('usersManagement.active'), value: 'Active' },
      { label: t('usersManagement.inactive'), value: 'Inactive' },
    ];
    this.notificationOptions = [
      { label: t('usersManagement.all'), value: '' },
      { label: t('usersManagement.pushOn'), value: 'true' },
      { label: t('usersManagement.pushOff'), value: 'false' },
    ];
    this.userTypeOptions = USER_TYPE_FILTER_OPTIONS.map((option) => ({
      ...option,
      label: t('usersManagement.types.' + (option.value || 'all')),
    }));
  }

  ngOnInit(): void {
    this.buildFilterOptions();
    this.#translate.onLangChange.subscribe(() => this.buildFilterOptions());
    this.releasePageScrollArtifacts();

    this.filterChanges$.pipe(debounceTime(this.FILTER_DEBOUNCE_MS), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      this.first = 0;
      this.fetchUsers();
    });

    this.#route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const userId = params.get(MANAGEMENT_USER_QUERY_KEY);
      if (userId) {
        this.openDrawerFromRoute(userId);
        return;
      }
      if (this.drawerVisible) {
        this.drawerVisible = false;
        this.selectedUser = null;
        this.closeNestedDrawers();
        this.cdr.markForCheck();
      }
    });

    this.drawerSubscriptions.add(
      this.#activitiesApi.getSportTypes().subscribe({
        next: (sportTypes) => {
          this.sportTypeConfigs = sportTypes;
          this.sportOptions = toTwoColumnOptions(sportTypes.map(({ label, value }) => ({ label, value })));
          this.cdr.markForCheck();
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.releasePageScrollArtifacts();
    this.drawerSubscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUserActivityOpen(activity: Activity) {
    this.userGroupDrawers?.closeNestedDrawersFromParent();
    this.userActivityDrawer?.markInitialized();
    this.drawerSubscriptions.add(
      this.#activitiesApi.getDetail(String(activity.id)).subscribe({
        next: (detail) => {
          this.userActivityDrawer?.open({ ...detail, detailLoaded: true });
          this.cdr.markForCheck();
        },
        error: () => {
          this.showToast('Failed to load activity details', 'error');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  onUserGroupOpen(group: Group) {
    this.userActivityDrawer?.close();
    this.userGroupDrawers?.openGroupFromExternal({
      id: String(group.id),
      title: group.title,
      sport: group.type,
    });
    this.cdr.markForCheck();
  }

  onUserActivityVisibilityChange(visible: boolean) {
    if (!visible) {
      this.scheduleNestedDrawerCleanup();
    }
    this.cdr.markForCheck();
  }

  onUserActivitySessionChange() {
    if (!this.isNestedManagementDrawerVisible()) {
      this.scheduleNestedDrawerCleanup();
    }
  }

  private scheduleNestedDrawerCleanup() {
    this.document.defaultView?.requestAnimationFrame(() => {
      this.cleanupNestedDrawerArtifacts();
      this.document.defaultView?.setTimeout(() => this.cleanupNestedDrawerArtifacts(), 180);
    });
  }

  private isNestedManagementDrawerVisible(): boolean {
    const activityDrawer = this.userActivityDrawer;
    return (
      !!activityDrawer?.visible ||
      !!activityDrawer?.deleteDialogVisible ||
      !!activityDrawer?.deleteDialogClosing ||
      !!this.userGroupDrawers?.isAnyModalVisible()
    );
  }

  hasNestedManagementDrawerOpen(): boolean {
    return this.isNestedManagementDrawerVisible();
  }

  onNestedGroupDrawerVisibilityChange() {
    this.cdr.markForCheck();
  }

  private cleanupNestedDrawerArtifacts() {
    if (this.isNestedManagementDrawerVisible()) {
      return;
    }

    this.userGroupDrawers?.cleanupModalArtifactsIfIdle();

    this.releasePageScrollArtifacts();

    if (this.document.querySelector('.p-sidebar-active')) {
      return;
    }

    this.document.querySelectorAll<HTMLElement>('body > .p-sidebar-mask').forEach((mask) => {
      mask.remove();
    });
  }

  private releasePageScrollArtifacts() {
    this.document.body.classList.remove('groups-modal-background-locked');
    this.document.body.style.removeProperty('--groups-modal-scroll-top');
    this.document.body.classList.remove('p-overflow-hidden');
    this.document.body.style.removeProperty('overflow');
    this.document.body.style.removeProperty('position');
    this.document.body.style.removeProperty('top');
    this.document.body.style.removeProperty('left');
    this.document.body.style.removeProperty('right');
    this.document.body.style.removeProperty('width');
  }

  private closeNestedDrawers() {
    this.userActivityDrawer?.close();
    this.userGroupDrawers?.closeNestedDrawersFromParent();
    this.scheduleNestedDrawerCleanup();
  }

  /** Keep /users-management?user=… — nested group/activity drawers must not change the route. */
  private syncUserDrawerUrl(userId: string | null) {
    const currentUserId = this.#route.snapshot.queryParamMap.get(MANAGEMENT_USER_QUERY_KEY);
    if (userId && currentUserId === userId) {
      return;
    }

    if (!userId && !currentUserId) {
      return;
    }

    const scrollY = this.document.defaultView?.scrollY ?? 0;
    const url = this.#router.serializeUrl(
      this.#router.createUrlTree([], {
        relativeTo: this.#route,
        queryParams: userId ? { [MANAGEMENT_USER_QUERY_KEY]: userId } : { [MANAGEMENT_USER_QUERY_KEY]: null },
        queryParamsHandling: 'merge',
      }),
    );
    this.#location.replaceState(url);
    this.restoreListScrollAfterUrlChange(scrollY);
  }

  private restoreListScrollAfterUrlChange(scrollY: number) {
    if (scrollY <= 0) {
      return;
    }

    this.document.defaultView?.requestAnimationFrame(() => {
      this.document.defaultView?.scrollTo(0, scrollY);
    });
  }

  get users(): UserDetail[] {
    return this.#store.users();
  }

  get totalUsers(): number {
    return this.#store.totalUsers();
  }

  get activeUsers(): number {
    return this.#store.proUsersCount();
  }

  get activePercent(): number {
    if (!this.totalUsers) return 0;
    return Math.round((this.activeUsers / this.totalUsers) * 100);
  }

  get paginationReportText(): string {
    return `Showing ${this.getPaginationFirst()} to ${this.getPaginationLast()} of ${this.totalUsers} results`;
  }

  getPaginationFirst(): number {
    return this.totalUsers ? this.first + 1 : 0;
  }

  getPaginationLast(): number {
    return this.totalUsers ? Math.min(this.first + this.pageSize, this.totalUsers) : 0;
  }

  showToast(message: string, type: 'success' | 'info' | 'error' = 'info', timeout = 4000) {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.cdr.markForCheck();
    }, timeout);
    this.cdr.markForCheck();
  }

  /** Debounced search only (filter panel uses Apply). */
  onSearchChanged(): void {
    this.filterChanges$.next();
  }

  exportingCsv = false;

  /**
   * Downloads the filtered list. The file is built by the API, so what is
   * exported always matches what the filters describe rather than the rows
   * that happen to be on screen.
   */
  exportCsv(): void {
    if (this.exportingCsv) {
      return;
    }
    this.exportingCsv = true;
    this.cdr.markForCheck();

    this.#store.exportUsersCsv(this.buildListFilters()).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        // Released straight away; the browser has already taken the data.
        URL.revokeObjectURL(url);
        this.exportingCsv = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.exportingCsv = false;
        this.cdr.markForCheck();
      },
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.first = 0;
    this.fetchUsers();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.applyTableSort(event.sortField, event.sortOrder);

    this.pageSize = event.rows ?? 10;
    if (this.sortChangedSinceLastFetch) {
      this.first = 0;
      this.page = 1;
      this.sortChangedSinceLastFetch = false;
    } else {
      this.first = event.first ?? 0;
      this.page = Math.floor(this.first / this.pageSize) + 1;
    }

    this.fetchUsers();
  }

  private sortChangedSinceLastFetch = false;

  private applyTableSort(
    sortField: TableLazyLoadEvent['sortField'] | string | undefined | null,
    sortOrder: TableLazyLoadEvent['sortOrder'] | number | undefined | null,
  ): void {
    const nextSortField = this.resolveSortField(sortField);
    const nextSortOrder = this.resolveSortOrder(sortOrder);

    if (nextSortField !== undefined && nextSortField !== this.sortField) {
      this.sortField = nextSortField;
      this.sortChangedSinceLastFetch = true;
    }
    if (nextSortOrder !== undefined && nextSortOrder !== this.sortOrder) {
      this.sortOrder = nextSortOrder;
      this.sortChangedSinceLastFetch = true;
    }
  }

  private fetchUsers(): void {
    this.#store.loadUsers(this.buildListFilters());
    this.cdr.markForCheck();
  }

  private buildListFilters(): UsersManagementListFilters {
    return {
      search: this.searchQuery,
      userType: this.selectedUserType || undefined,
      status: this.selectedStatus || undefined,
      createdFrom: this.toDateParam(this.startDate),
      createdTo: this.toDateParam(this.endDate),
      loginFrom: this.toDateParam(this.startLoginDate),
      loginTo: this.toDateParam(this.endLoginDate),
      city: this.cityFilter.trim() || undefined,
      completionMin: this.completionMin ?? undefined,
      completionMax: this.completionMax ?? undefined,
      sortBy: this.mapSortFieldToApi(this.sortField),
      sortOrder: this.sortOrder === 1 ? 'asc' : 'desc',
      page: this.page,
      pageSize: this.pageSize,
    };
  }

  private resolveSortField(sortField: TableLazyLoadEvent['sortField']): string | undefined {
    if (sortField === undefined || sortField === null) {
      return undefined;
    }
    return Array.isArray(sortField) ? sortField[0] : sortField;
  }

  private resolveSortOrder(sortOrder: TableLazyLoadEvent['sortOrder'] | number | undefined | null): 1 | -1 | undefined {
    if (sortOrder === undefined || sortOrder === null) {
      return undefined;
    }
    const value = Array.isArray(sortOrder) ? sortOrder[0] : sortOrder;
    if (value === 0) {
      return undefined;
    }
    return value === 1 ? 1 : -1;
  }

  private mapSortFieldToApi(field: string): UsersManagementListSortBy {
    const mapping: Record<string, UsersManagementListSortBy> = {
      name: 'name',
      email: 'email',
      createdAt: 'createdAt',
      'userType.label': 'userType',
      status: 'status',
      lastLoginDate: 'lastLoginDate',
      pushNotification: 'pushNotification',
    };
    return mapping[field] ?? 'createdAt';
  }

  /**
   * Colour band for the completion bar. Thresholds match the ranges the
   * Enhanced User Management Table ticket asks to filter on.
   */
  /** Short label shown in the cell. Written out, never colour alone. */
  temperatureLabel(temperature?: string): string {
    switch (temperature) {
      case 'hot':
        return this.#translate.instant('usersManagement.temperature.hot');
      case 'warm':
        return this.#translate.instant('usersManagement.temperature.warm');
      case 'cool':
        return this.#translate.instant('usersManagement.temperature.cool');
      case 'frozen':
        return this.#translate.instant('usersManagement.temperature.frozen');
      case 'never':
        return this.#translate.instant('usersManagement.temperature.never');
      default:
        return '-';
    }
  }

  /** Says what the label means, since "cool" alone does not. */
  temperatureTooltip(temperature?: string): string {
    switch (temperature) {
      case 'hot':
        return 'Created or joined an activity in the last 30 days';
      case 'warm':
        return 'Last activity between 1 and 3 months ago';
      case 'cool':
        return 'Last activity between 3 and 12 months ago';
      case 'frozen':
        return 'Last activity over a year ago';
      case 'never':
        return 'Never created nor joined an activity';
      default:
        return 'Not computed';
    }
  }

  completionClass(value?: number): string {
    const score = value ?? 0;
    if (score >= 80) return 'completion-cell__fill--high';
    if (score >= 50) return 'completion-cell__fill--medium';
    return 'completion-cell__fill--low';
  }

  private toDateParam(date: Date | null): string | null {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  hasValidUserType(user: UserDetail): boolean {
    return !!user.userType?.isValid;
  }

  getStatusBadgeClass(status: string): string {
    return status === 'Active' ? 'badge--active' : 'badge--inactive';
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  resetFilters() {
    this.startDate = null;
    this.endDate = null;
    this.startLoginDate = null;
    this.endLoginDate = null;
    this.selectedUserType = '';
    this.selectedStatus = '';
    this.searchQuery = '';
    this.cityFilter = '';
    this.completionMin = null;
    this.completionMax = null;
    this.sortField = 'createdAt';
    this.sortOrder = -1;
    this.page = 1;
    this.first = 0;
    this.fetchUsers();
  }

  private readonly routerForSheet = inject(Router);

  /** The member whose sheet the right panel shows; null keeps it closed. */
  sheetUserId: string | null = null;

  /** A click on the person slides their full sheet in from the right, the
   * same drawer pattern as the rest of this screen; editing stays on the
   * pencil. */
  openSheet(user: UserDetail) {
    this.sheetUserId = String(user.id);
  }

  closeSheet() {
    this.sheetUserId = null;
  }

  openDrawer(user: UserDetail) {
    if (this.hasNestedManagementDrawerOpen()) {
      this.closeNestedDrawers();
      this.cdr.markForCheck();
      return;
    }

    this.drawerInitialMode = 'view';
    this.drawerVisible = true;
    this.syncUserDrawerUrl(String(user.id));
    this.loadUserDetail(String(user.id));
  }

  onDrawerVisibleChange(visible: boolean) {
    if (!visible) {
      this.drawerInitialMode = 'view';
      this.closeNestedDrawers();
      this.syncUserDrawerUrl(null);
      return;
    }
  }

  private openDrawerFromRoute(userId: string) {
    if (this.drawerVisible && String(this.selectedUser?.id) === userId) {
      return;
    }
    this.drawerInitialMode = 'view';
    this.drawerVisible = true;
    this.loadUserDetail(userId);
  }

  openDrawerInEditMode(user: UserDetail) {
    this.drawerInitialMode = 'edit';
    this.drawerVisible = false;
    this.syncUserDrawerUrl(String(user.id));
    this.loadUserDetail(String(user.id), { revealDrawer: true });
  }

  openDeleteConfirm(user: UserDetail) {
    this.selectedUser = user;
    this.drawerVisible = false;
    this.deleteConfirmed = false;
    this.deletingUser = false;
    this.showDeleteBlockedDialog = false;
    this.deleteBlockedContent = null;
    this.showDeleteDialog = false;
    this.creditOnDeletion = null;
    this.cdr.markForCheck();

    this.#store.getDeleteEligibility(String(user.id)).subscribe({
      next: (eligibility) => {
        if (isDeleteUserBlocked(eligibility)) {
          this.deleteBlockedContent = buildDeleteUserBlockedDialogContent(eligibility.blockers);
          this.showDeleteBlockedDialog = true;
          this.cdr.markForCheck();
          return;
        }
        this.creditOnDeletion = readCreditOnDeletion(eligibility);
        this.showDeleteDialog = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.showToast('Failed to check delete eligibility', 'error');
        this.cdr.markForCheck();
      },
    });
  }

  onDeleteDialogCancelled() {
    this.showDeleteDialog = false;
    this.deleteConfirmed = false;
    this.deletingUser = false;
    this.cdr.markForCheck();
  }

  onDeleteBlockedDialogClosed() {
    this.showDeleteBlockedDialog = false;
    this.deleteBlockedContent = null;
    this.cdr.markForCheck();
  }

  suspendSelectedUser(reason: AccountSuspensionReason) {
    const user = this.selectedUser;
    if (!user || this.suspendingUser) return;
    const userId = String(user.id);
    this.suspendingUser = true;
    this.cdr.markForCheck();

    this.#store.suspendAccount(userId, reason).subscribe({
      next: () => {
        this.suspendingUser = false;
        this.showDeleteBlockedDialog = false;
        this.deleteBlockedContent = null;
        this.showToast(this.#translate.instant('usersManagement.suspendSuccess'), 'success');
        this.loadUserDetail(userId);
      },
      // The row picked in the list does not carry the suspension, so the button
      // can show for an account already suspended: the API answers 409 then.
      error: (err: { status?: number; error?: { message?: string } }) => {
        this.suspendingUser = false;
        const already = err?.status === 409 && err?.error?.message === 'already_suspended';
        this.showToast(
          this.#translate.instant(already ? 'dialogs.alreadySuspended' : 'usersManagement.suspendFailed'),
          already ? 'info' : 'error',
        );
        if (already) this.loadUserDetail(userId);
      },
    });
  }

  liftSuspension(user: UserDetail) {
    const userId = String(user.id);
    this.#store.liftSuspension(userId).subscribe({
      next: () => {
        this.showToast(this.#translate.instant('usersManagement.liftSuccess'), 'success');
        this.loadUserDetail(userId);
      },
      error: () => {
        this.showToast(this.#translate.instant('usersManagement.liftFailed'), 'error');
      },
    });
  }

  confirmDelete() {
    if (!this.deleteConfirmed || !this.selectedUser || this.deletingUser) return;
    this.executeDeleteUser(this.selectedUser, {
      closeDialogOnStart: false,
      onSuccess: () => {
        this.showDeleteDialog = false;
        this.deleteConfirmed = false;
        this.selectedUser = null;
      },
    });
  }

  editUser(user: UserDetail) {
    const userId = String(user.id);
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    const normalized = {
      ...user,
      name: fullName || (user.hasProfile === false ? user.email : user.name),
    };
    this.#store.users.update((list) => list.map((u) => (String(u.id) === userId ? { ...normalized } : u)));
    if (this.selectedUser && String(this.selectedUser.id) === userId) {
      this.selectedUser = { ...normalized };
    }
    this.cdr.markForCheck();
  }

  private executeDeleteUser(user: UserDetail, options: { closeDialogOnStart?: boolean; onSuccess?: () => void } = {}) {
    const userId = String(user.id);
    const userName = user.name;
    this.deletingUser = true;
    this.cdr.markForCheck();

    this.#store.deleteUser(userId).subscribe({
      next: () => {
        this.deletingUser = false;
        this.#store.users.update((list) => list.filter((u) => String(u.id) !== userId));
        this.#store.totalUsers.update((n) => Math.max(0, n - 1));
        if (user.userType?.isValid) {
          this.#store.proUsersCount.update((n) => Math.max(0, n - 1));
        }
        options.onSuccess?.();
        this.toastUserName = userName;
        this.showDeleteSuccessToast();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.deletingUser = false;
        this.handleDeleteError(err);
        this.cdr.markForCheck();
      },
    });
  }

  private handleDeleteError(error: unknown) {
    const body = extractDeleteUserErrorBody(error);
    if (body?.blockers?.length) {
      this.showDeleteDialog = false;
      this.deleteConfirmed = false;
      this.deleteBlockedContent = buildDeleteUserBlockedDialogContent(body.blockers);
      this.showDeleteBlockedDialog = true;
      return;
    }

    this.toastUserName = '';
    this.showToast(getDeleteUserErrorMessage(error), 'error');
  }

  private showDeleteSuccessToast() {
    this.toastMessage = 'delete-success';
    this.toastType = 'success';
    this.toastVisible = true;
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.cdr.markForCheck();
    }, 4000);
    this.cdr.markForCheck();
  }

  private loadUserDetail(userId: string, options: { revealDrawer?: boolean } = {}) {
    this.selectedUser = this.users.find((u) => String(u.id) === userId) ?? null;
    this.#store.loadUserDetail(userId).subscribe({
      next: (res) => {
        if (res?.user) {
          this.selectedUser = this.#store.normalizeUser(res.user);
          if (options.revealDrawer) {
            this.drawerVisible = true;
          }
          this.cdr.markForCheck();
        }
      },
      error: () => {
        if (options.revealDrawer) {
          this.drawerVisible = false;
          this.drawerInitialMode = 'view';
        }
        this.showToast('Failed to load user details', 'error');
        this.cdr.markForCheck();
      },
    });
  }
}
